import { useState, useEffect, useCallback, ReactNode } from 'react'
import { AuthContext, type User } from './auth-context-definition'
import { fetchCurrentUser, logout as apiLogout } from '@/services/authService'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const loadUser = useCallback(async () => {
    try {
      const currentUser = await fetchCurrentUser()
      setUser(currentUser)
    } catch {
      // Not authenticated or session expired
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // On mount, try to fetch user (cookie is sent automatically)
  useEffect(() => {
    void loadUser()
  }, [loadUser])

  // Login just stores the user in state
  // The cookie is already set by the server response
  const login = useCallback((userData: User) => {
    setUser(userData)
    setLoading(false)
  }, [])

  const logout = useCallback(async () => {
    setUser(null)
    try {
      await apiLogout()
    } catch (error) {
      console.warn('Failed to notify API about logout', error)
    }
  }, [])

  const refetchUser = useCallback(async () => {
    setLoading(true)
    await loadUser()
  }, [loadUser])

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: user !== null,
      login,
      logout,
      loading,
      refetchUser
    }}>
      {children}
    </AuthContext.Provider>
  )
}
